import React, { useState } from "react";
import { Card, CardBody, Row, Col, Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Avatar, Typography } from "@mui/material";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import StarIcon from "@mui/icons-material/Star";
import helpers from "utils/helpers";
import userServices from "services/userServices";
import "assets/css/custom.css";
import UserActions from "../components/UserActions";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import ProfilePictureModal from "./ProfilePictureModal";
import UserListingsModal from "../userTable/UserListingsModal";
import { UncontrolledTooltip } from "reactstrap";
import UserOrdersModal from "../userTable/UserOrdersModal";
import { Switch } from "@mui/material";
import DeactivateReasonModal from "modals/DeactivateReasonModal";
import { useAuth } from "context/AuthContext";

const UserProfileHeader = ({
  localUserData,
  profilePicUrl,
  setOpenModal,
  userActions,
  setLocalUserData,
  handleToggleAccount,
  statusLoadingId,
  setStatusLoadingId,
}) => {
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [loading, setLoading] = useState(null);
  const [reasonModal, setReasonModal] = useState(false);
  const [selectedUserForDeactivation, setSelectedUserForDeactivation] = useState(null);
  const [reinstating, setReinstating] = useState(false);

  const { permission } = useAuth();
  const canUser = permission?.permissions?.modules?.users;

  const { notify } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [listingModalOpen, setListingModalOpen] = useState(false);
  const [listingType, setListingType] = useState(null);
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const openListingsModal = async (type) => {
    try {
      setListingType(type);
      setListingModalOpen(true);
      setIsLoading(true);

      let res;
      if (type === "sender") {
        res = await userServices.getSenderListingById(localUserData._id);
        const dataObj = res?.data || res;
        setListings(dataObj?.userSenderListings || dataObj?.data?.userSenderListings || []);
      } else {
        res = await userServices.getTravelerListingById(localUserData._id);
        const dataObj = res?.data || res;
        setListings(dataObj?.userTravelerListings || dataObj?.data?.userTravelerListings || []);
      }
    } catch {
      setListings([]);
    } finally {
      setIsLoading(false);
    }
  };

  const [ordersModalOpen, setOrdersModalOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const openOrdersModal = async () => {
    try {
      setOrdersModalOpen(true);
      setOrdersLoading(true);

      const res = await userServices.fetchUserOrders(localUserData._id);
      const dataObj = res?.data || res;
      const ordersList = Array.isArray(dataObj) ? dataObj : (dataObj?.orders || []);

      const formattedOrders = ordersList.map((order) => ({
        ...order,
        sender_listing_id: order.sender_listing_id || {},
        traveler_listing_id: order.traveler_listing_id || {},
        status: order.status || "pending",
        _id: order._id,
      }));

      setOrders(formattedOrders);
    } catch (err) {
      console.error("Error fetching user orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleProfilePictureModeration = async (status, adminRemarks) => {
    try {
      setLoading(status);

      const res = await userServices.userProfileVerification(
        localUserData._id,
        { status, adminRemarks }
      );

      const dataObj = res?.data?.data || res?.data || res;
      if (dataObj?.success) {
        setLocalUserData(dataObj.user || dataObj.data);
        notify("success", t(dataObj.messageKey || "page.users.toast.updated", dataObj.message));
        setReviewModalOpen(false);
      } else {
        notify("error", t(dataObj?.messageKey || dataObj?.error || "page.users.toast.updateFailed", dataObj?.message));
      }
    } catch {
      notify("error", t("page.users.toast.updateFailed", "Failed to update profile picture status"));
    } finally {
      setLoading(null);
    }
  };

  const isDeleted = localUserData?.isDeleted;

  const handleReinstate = async () => {
    if (reinstating) return;
    try {
      setReinstating(true);
      const res = await userServices.reinstateUser(localUserData._id);
      const dataObj = res?.data?.data || res?.data || res;
      if (dataObj?.success || dataObj?._id) {
        const updatedUser = dataObj.data || dataObj.user || dataObj;
        setLocalUserData(updatedUser);
        notify("success", t(res?.data?.messageKey || "page.userListings.toast.userReinstated", "User reinstated successfully"));
      } else {
        notify("error", t(dataObj?.error || "page.users.toast.updateFailed", dataObj?.message || "Failed to reinstate user"));
      }
    } catch (err) {
      const errData = err?.response?.data;
      notify("error", t(errData?.error || "page.users.toast.updateFailed", errData?.message || "Failed to reinstate user"));
    } finally {
      setReinstating(false);
    }
  };

  return (
    <div>
      <div className="mb-2 d-flex">
        <Button size="sm" className="ml-auto" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <Card className="shadow-sm mb-4 p-3">
        <CardBody>
          <Row className="align-items-center align-items-md-start">
            <Col xs="12" md="3" className="text-center mb-3 mb-md-0">
              <div id="profile-pic">
                {localUserData?.profilePictureUrl ? (
                  <Avatar
                    src={profilePicUrl}
                    sx={{
                      width: 110,
                      height: 110,
                      cursor: isDeleted ? "" : "pointer",
                      margin: "0 auto",
                    }}
                    onClick={() => {
                      if (!isDeleted) setReviewModalOpen(true);
                    }}
                  />
                ) : (
                  <Avatar
                    {...helpers.stringAvatar(
                      `${localUserData?.firstName || ""} ${localUserData?.lastName || ""}`
                    )}
                    sx={{
                      width: 110,
                      height: 110,
                      fontSize: 28,
                      cursor: isDeleted ? "" : "pointer",
                      margin: "0 auto",
                    }}
                  />
                )}
              </div>

              {isDeleted && (
                <UncontrolledTooltip target="profile-pic">
                  {t("page.users.actions.userDeleted", "User is deleted")}
                </UncontrolledTooltip>
              )}
            </Col>

            <Col xs="12" md="6">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center align-items-md-center gap-2">
                <Typography
                  variant="h5"
                  fontWeight={600}
                    className="d-flex align-items-center justify-content-center justify-content-md-start flex-wrap"
                  >
                  {localUserData?.firstName} {localUserData?.lastName}
                  {localUserData?.isVerified && (
                    <VerifiedOutlinedIcon
                      className="verified-icon ms-1"
                      fontSize="small"
                    />
                  )}
                </Typography>

                <Button
                  className="mt-2 mt-md-0"
                  color="warning"
                  size="sm"
                  onClick={() => setOpenModal(true)}
                >
                  {t("page.user-profile.header.showReviews", "Show Reviews")}
                </Button>
              </div>

                <Typography color="text.secondary" className="mb-3 text-center text-md-left">
                {localUserData?.email}
              </Typography>

              <Row className="text-center text-md-left mt-3">
                <Col xs="6" md="3" className="mb-3 mb-md-0">
                  <h5
                    id="sender-listings-count"
                    className="mb-0 cursor-pointer text-primary"
                    onClick={() => openListingsModal("sender")}
                  >
                    {localUserData?.listings?.senderCount ?? 0}
                  </h5>
                  <small className="text-muted">
                    {t("page.user-profile.header.senderListings", "Sender Listings")}
                  </small>
                  <UncontrolledTooltip target="sender-listings-count">
                    {t("page.user-profile.header.viewSenderListings", "View sender listings")}
                  </UncontrolledTooltip>
                </Col>

                <Col xs="6" md="3" className="mb-3 mb-md-0">
                  <h5
                    id="traveler-listings-count"
                    className="mb-0 cursor-pointer text-primary"
                    onClick={() => openListingsModal("traveler")}
                  >
                    {localUserData?.listings?.travelerCount ?? 0}
                  </h5>
                  <small className="text-muted">
                    {t("page.user-profile.header.travelerListings", "Traveler Listings")}
                  </small>
                  <UncontrolledTooltip target="traveler-listings-count">
                    {t("page.user-profile.header.viewTravelerListings", "View traveler listings")}
                  </UncontrolledTooltip>
                </Col>

                <Col xs="6" md="3">
                  <h5
                    id="orders-count"
                    className="mb-0 cursor-pointer text-primary"
                    onClick={openOrdersModal}
                  >
                    {localUserData?.listings?.orderCount ?? 0}
                  </h5>
                  <small className="text-muted">
                    {t("page.user-profile.header.deliveries", "Deliveries")}
                  </small>
                  <UncontrolledTooltip target="orders-count">
                    {t("page.user-profile.header.viewDeliveries", "View Deliveries")}
                  </UncontrolledTooltip>
                </Col>

                <Col xs="6" md="3">
                  <div className="d-flex justify-content-center justify-content-md-start align-items-center">
                    <h5 className="mb-0">
                      {localUserData?.averageRating ?? "-"}
                    </h5>
                    {localUserData?.averageRating &&
                      !isNaN(localUserData.averageRating) && (
                        <StarIcon className="star-icon ms-1" />
                      )}
                  </div>
                  <small className="text-muted">
                    {t("page.user-profile.header.rating", "Rating")}
                  </small>
                </Col>
              </Row>
            </Col>

            <Col
              xs="12"
              md="3"
              className="d-flex justify-content-center justify-content-md-end align-items-center mt-3 mt-md-0 gap-3"
            >
              {isDeleted && canUser?.update && (
                <Button
                  color="success"
                  size="sm"
                  disabled={reinstating}
                  onClick={handleReinstate}
                >
                  {reinstating
                    ? t("page.users.actions.reinstating", "Reinstating...")
                    : t("page.users.actions.reinstate", "Reinstate User")}
                </Button>
              )}
              {localUserData?._id && (
                <div className="d-flex align-items-center gap-4">
                  <div id={`status-toggle-wrapper-${localUserData._id}`} className="mr-2">
                    <Switch
                      size="small"
                      checked={localUserData?.accountStatus === "active"}
                      disabled={!canUser?.update || isDeleted || statusLoadingId === localUserData._id}
                      style={(!canUser?.update || isDeleted) ? { pointerEvents: "none" } : {}}
                      onChange={(e) => {
                        if (!canUser?.update) return;

                        if (e.target.checked) {
                          handleToggleAccount(localUserData, true);
                        } else {
                          setStatusLoadingId(localUserData._id);
                          setSelectedUserForDeactivation(localUserData);
                          setReasonModal(true);
                        }
                      }}
                    />
                  </div>
                  <UncontrolledTooltip target={`status-toggle-wrapper-${localUserData._id}`}>
                    {isDeleted 
                      ? t("page.users.actions.userDeleted", "User Deleted") 
                      : localUserData?.accountStatus === "active"
                        ? t("page.users.actions.deactivate", "Deactivate Account")
                        : t("page.users.actions.activate", "Activate Account")
                    }
                  </UncontrolledTooltip>
                  {localUserData?._id && (
                    <UserActions
                      row={localUserData}
                      onDelete={userActions?.handleDelete}
                      onReset={userActions?.handleResetPassword}
                      onEdit={userActions?.handleEdit}
                      isLoading={statusLoadingId === localUserData?._id}
                    />
                  )}
                </div>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      <ProfilePictureModal
        isOpen={reviewModalOpen}
        toggle={() => setReviewModalOpen(false)}
        user={localUserData}
        profilePicUrl={profilePicUrl}
        loading={loading}
        onModerate={handleProfilePictureModeration}
      />

      <UserListingsModal
        isOpen={listingModalOpen}
        toggle={() => setListingModalOpen(false)}
        listings={listings}
        isLoading={isLoading}
        type={listingType}
      />

    <UserOrdersModal
        isOpen={ordersModalOpen}
        toggle={() => setOrdersModalOpen(false)}
        orders={orders}
        isLoading={ordersLoading}
      />

      <DeactivateReasonModal
        isOpen={reasonModal}
        toggle={() => {
          setReasonModal(false);
          setSelectedUserForDeactivation(null);
          setStatusLoadingId(null);
        }}
        onConfirm={(reasonText, selectedReason) => {
          if (!selectedUserForDeactivation) return Promise.resolve();

          return handleToggleAccount(
            selectedUserForDeactivation,
            false,
            reasonText,
            selectedReason,
            () => {
              setReasonModal(false);
              setSelectedUserForDeactivation(null);
            }
          );
        }}
      />
    </div>
  );
};

export default UserProfileHeader;
